import moment from 'moment';
import {
  format,
  formatISO,
  formatISO9075,
  formatRFC7231,
  parseISO,
  addSeconds,
  intervalToDuration,
  formatDuration,
} from 'date-fns';

function pluralize(time, label) {
  if (time === 1) {
    return time + label;
  }
  return `${time + label}s`;
}

/* eslint no-bitwise: ["error", { "allow": ["~"] }] */
export function timeAgo(time) {
  const between = Date.now() / 1000 - Number(time);
  if (between < 3600) {
    return pluralize(~~(between / 60), ' minute');
  }
  if (between < 86400) {
    return pluralize(~~(between / 3600), ' hour');
  }
  return pluralize(~~(between / 86400), ' day');
}

export const utilityCapitalize = (word) => {
  if (word === undefined) return;
  if (word && typeof word === 'string') {
    return word?.charAt(0)?.toUpperCase() + word?.slice(1);
  }
};

export const utilityFormatDateAndTime = (date) => {
  if (date === undefined || date === null) return;
  const formattedDate = date.replace('Etc/GMT', '');
  return `${moment(formattedDate).format('L LT')}`; // 01/22/2019 4:00 PM
};

export const utilityFormatDateAndTimeUTC = (date) => {
  if (date === undefined || date === null) return;
  const formattedDate = date.replace('Etc/GMT', '');
  return `${moment.utc(formattedDate).format('L LT')}`; // 01/22/2019 4:00 PM
};

export const utilityFormatDateAndTimeDB = (date) => {
  if (date === undefined || date === null) return;
  const dateFormat = formatISO9075(parseISO(date), { representation: 'date' });
  const dbDateFormat = formatRFC7231(parseISO(date));
  const splitGMTTime = dbDateFormat.split(/(\d+:\d+:\d+\sGMT)/)[1];
  return `${dateFormat} ${splitGMTTime}`; // 2021-08-24 22:45:31 GMT
};

export const utilityFormatDateAndTimeOpen = (date) => {
  if (date === undefined || date === null) return 'Open';
  return `${moment(date).format('L LT')}`; // 01/22/2019 4:00 PM
};

export const utilityFormatDateMonthAndYear = (date) => {
  if (date === undefined || date === null) return 'Open';
  return `${moment(date).format('L')}`; // 01/18/2022
};

export const utilityFormatDateMY = (date) => {
  if (date === undefined || date === null) return;
  // return `${format(parseISO(date), 'P')}`; // 01/18/2022
  return `${format(parseISO(date), 'PP')}`; // Jan 22, 2022
};

export const utilityFormatTimeToMonthAndYear = (time) => {
  if (time === undefined || time === 'N/A') return 'N/A';
  if (time === 'Just Now') return time;
  return moment.utc(String(time), 'YYYY-MM-DDTHH:mm:ssZ').format('MMM D, YYYY'); // Oct 29, 2018
};

export const utilityFormatDateFriendly = (date) => {
  if (date === undefined || date === 'N/A') return 'N/A';
  return format(parseISO(date), 'PPP'); // October 29, 2022
};

export const utilityFormatDate = (date) => {
  if (date === undefined || date === '') return '-';
  return format(parseISO(date), 'MM/dd/yyyy'); // 01/22/2019
};

export const utilityFormatTime = (time) => {
  if (time === undefined) return;
  return moment(time).format('LT');
};

export const utilityFromDate = (d) => {
  if (d === '' || d === undefined) return;
  if (moment(d).isBefore()) {
    return `Expired: ${moment(d).format('L LT')}`;
  }
  return `Expires: ${moment(d).format('L LT')}`;
};

export const utilityPresence = (val) => {
  const newVal = val?.toString();
  if (newVal && newVal !== 'undisclosed') return val;
  return '-';
};

export const utilityHandicapPresence = (val) => {
  if (val || val === 0) return val;
  return '-';
};

export const utilityHandicapFormat = (h) => {
  if (h === undefined || h === null) return;
  if (h === '-') return h;
  const hSplit = h.toString().split('-');
  if (hSplit.length > 1) {
    return `+${hSplit[1]}`;
  }
  return hSplit[0];
};

export const utilityPercentage = (v) => {
  if (v || v === 0) return `${(v * 100).toFixed(1)}%`;
  return '-';
};

export const utilityValueCheck = (v) => {
  if (v === undefined || v === null || v?.toLowerCase() === 'undisclosed') return 'N/A';
  return v;
};

export const utilityFormatCurrency = (value) => {
  if (typeof value !== 'number' || isNaN(value)) {
    return '-';
  }
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
  return formatter.format(value);
};

export const utilityLastSix = (s) => {
  if (!s || s === undefined) return '';
  const d = s?.toString();
  return d.substr(d.length - 6);
};

export const utilityFirstEight = (s) => {
  if (!s || s === undefined) return '';
  const d = s?.toString();
  return d.substr(0, 8);
};

export const utilityYesNo = (v) => {
  if (v === undefined) return;
  if (v) {
    return `Yes`;
  }
  return `No`;
};

export const utilityToFixedTwoDecimal = (v) => {
  if (v === null || v === undefined) return;
  if (v.toString().length > 2) {
    return v.toFixed(2);
  }
  return v;
};

export const utilityToFixedOneDecimal = (v) => {
  if (v === null || v === undefined) return;
  if (v === 'N/A') return v;
  if (v === 0 || v === 0.0 || v === '0' || v === '0.0') return 0;
  const data = v.toString();
  if (data.length > 2) {
    if (data.split('+').length > 1) {
      return Number(data.split('+')?.[1]).toFixed(1);
    }
    return Number(data).toFixed(1);
  }
  return v;
};

export const utilityScoreCheck = (s) => {
  if (s === null || s === undefined) {
    return 'DNF';
  }
  if (s === -1 || s === 999999) {
    return 'DNQ';
  }
  return s;
};

export const utilityFormatHHmmss = (time) => {
  if (time === undefined) return '-';
  const arr = time.split(':');
  arr[2] = Math.round(arr[2]).toLocaleString('en-US', {
    minimumIntegerDigits: 2,
  });
  return arr.join(':');
};
